import Cookies from "js-cookie";

const DEFAULT_PERMISSIONS = {
    canCreateEmail: false,
    canUseEmailSender: false,
    canUseEmailValidator: false,
    canManageFollowUps: false,
    canEditEmailTemplates: false,
    canSendSms: false,
    canReceiveSms: false,
    canEditSmsTemplates: false,
};

export function parseUserAccess() {
    const raw = Cookies.get("user_access");
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (parsed === "all") return "all";
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

export function parseUserPermissions() {
    if (hasFullAccess()) {
        return {
            canCreateEmail: true,
            canUseEmailSender: true,
            canUseEmailValidator: true,
            canManageFollowUps: true,
            canEditEmailTemplates: true,
            canSendSms: true,
            canReceiveSms: true,
            canEditSmsTemplates: true,
        };
    }
    const raw = Cookies.get("user_permissions");
    if (!raw) return { ...DEFAULT_PERMISSIONS };
    try {
        const parsed = JSON.parse(raw);
        return { ...DEFAULT_PERMISSIONS, ...parsed };
    } catch {
        return { ...DEFAULT_PERMISSIONS };
    }
}

export function hasFullAccess() {
    return Cookies.get("user_role") === "Administrator" || parseUserAccess() === "all";
}

function getCountryAccess(country) {
    if (hasFullAccess()) return { leads: true, emailIds: [], email: true };
    const access = parseUserAccess();
    const row = access.find((a) => a.country === country);
    if (!row) return { leads: false, emailIds: [], email: false };
    const emailIds = Array.isArray(row.emailIds) ? row.emailIds : [];
    const hasEmail =
        emailIds.length > 0 || !!row.email || !!row.sendEmail || !!row.receiveEmail;
    return {
        leads: !!row.leads,
        emailIds,
        email: hasEmail,
    };
}

export function canViewLeadsForCountry(country) {
    return getCountryAccess(country).leads;
}

export function canUseEmailForCountry(country) {
    return getCountryAccess(country).email;
}

export function canSendEmailForCountry(country) {
    return canUseEmailForCountry(country);
}

export function canReceiveEmailForCountry(country) {
    return canUseEmailForCountry(country);
}

export function hasAnyEmailAccess() {
    if (hasFullAccess()) return true;
    return parseUserAccess().some((a) => {
        const ids = Array.isArray(a.emailIds) ? a.emailIds : [];
        return ids.length > 0 || !!a.email || !!a.sendEmail || !!a.receiveEmail;
    });
}

export function hasAnySendEmailAccess() {
    const perms = parseUserPermissions();
    return hasFullAccess() || (hasAnyEmailAccess() && perms.canUseEmailSender);
}

export function hasAnyReceiveEmailAccess() {
    return hasAnyEmailAccess();
}

export function canCreateEmailAccounts() {
    return parseUserPermissions().canCreateEmail;
}

export function canUseEmailSender() {
    return parseUserPermissions().canUseEmailSender;
}

export function canUseEmailValidator() {
    return parseUserPermissions().canUseEmailValidator;
}

export function canManageFollowUps() {
    return parseUserPermissions().canManageFollowUps;
}

export function canEditEmailTemplates() {
    return parseUserPermissions().canEditEmailTemplates;
}

export function canSendSms() {
    return hasFullAccess() || parseUserPermissions().canSendSms;
}

export function canReceiveSms() {
    return hasFullAccess() || parseUserPermissions().canReceiveSms;
}

export function canEditSmsTemplates() {
    return hasFullAccess() || parseUserPermissions().canEditSmsTemplates;
}

export function hasAnySmsAccess() {
    const perms = parseUserPermissions();
    return hasFullAccess() || perms.canSendSms || perms.canReceiveSms;
}

export function getLeadCountries(activeCountries) {
    if (hasFullAccess()) return activeCountries;
    const access = parseUserAccess();
    const allowed = new Set(access.filter((a) => a.leads).map((a) => a.country));
    return activeCountries.filter((c) => allowed.has(c.Country));
}

export function getVisibleCountries(activeCountries) {
    if (hasFullAccess()) return activeCountries;
    const access = parseUserAccess();
    const allowed = new Set();
    access.forEach((a) => {
        const ids = Array.isArray(a.emailIds) ? a.emailIds : [];
        if (a.leads || ids.length > 0 || a.email || a.sendEmail || a.receiveEmail) {
            allowed.add(a.country);
        }
    });
    return activeCountries.filter((c) => allowed.has(c.Country));
}

export function saveUserAccessCookie(countryAccess, expires) {
    const value =
        countryAccess === "all" ? "all" : JSON.stringify(countryAccess || []);
    Cookies.set("user_access", value, { expires });
}

export function saveUserPermissionsCookie(permissions, expires) {
    if (!permissions || typeof permissions !== "object") return;
    Cookies.set("user_permissions", JSON.stringify({ ...DEFAULT_PERMISSIONS, ...permissions }), {
        expires,
    });
}

export const EMPTY_USER_PERMISSIONS = { ...DEFAULT_PERMISSIONS };
